import React, { useState } from 'react';
import { ENRICHERS, enricherById, spendOf, spendSummary } from '../../lib/enrichers';

// DEPRECATED admin machinery — provider/caps/enricher controls, kept verbatim under
// the new EnrichPanel's Advanced accordion for operators. The user-facing flow is one
// "Enrich my data" button; these are the raw levers behind it.
const TEAL = '#00C6AE';

export default function AdvancedPipelineControls({ onRunEnrichment, onRunEnricher, onStartImport, busy }) {
  const [provider, setProvider] = useState('deterministic');
  const [companyCap, setCompanyCap] = useState('50');
  const [peopleCap, setPeopleCap] = useState('');

  // "Run one enricher" — a bounded run of a single step, so a change can be tried
  // without paying for the other six.
  const [enricher, setEnricher] = useState('company_linkedin');
  const [oneProvider, setOneProvider] = useState('deterministic');
  const [oneLimit, setOneLimit] = useState('25');
  const oneSpend = spendOf(enricher, oneProvider);
  const selected = enricherById(enricher);

  return (
    <div className="space-y-6">
      <section className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
        <h2 className="text-sm font-semibold text-gray-900">Import</h2>
        <p className="text-xs text-gray-500 mt-1">
          Re-read every record from the source and clean it. Safe to run again — records are matched, not duplicated.
        </p>
        <button
          type="button"
          onClick={onStartImport}
          disabled={busy}
          className="mt-3 px-4 py-2 rounded-lg text-sm font-semibold text-white disabled:opacity-60 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
          style={{ background: TEAL, '--tw-ring-color': TEAL }}
        >
          {busy ? 'Running…' : 'Import again'}
        </button>
      </section>

      <section className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
        <h2 className="text-sm font-semibold text-gray-900">Enrich</h2>
        <p className="text-xs text-gray-500 mt-1">
          Fill the gaps the ATS left — websites, industries, locations, LinkedIn. Caps bound what you spend on
          external providers.
        </p>
        <div className="flex flex-wrap items-end gap-4 mt-4">
          <label className="flex flex-col text-xs text-gray-500">
            Provider
            <select
              value={provider}
              onChange={(e) => setProvider(e.target.value)}
              disabled={busy}
              className="mt-1 border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-900 bg-white"
            >
              <option value="deterministic">deterministic (no spend)</option>
              <option value="openai">openai</option>
              <option value="ollama">ollama</option>
            </select>
          </label>
          <label className="flex flex-col text-xs text-gray-500">
            Company cap
            <input
              value={companyCap}
              onChange={(e) => setCompanyCap(e.target.value)}
              disabled={busy}
              placeholder="default"
              inputMode="numeric"
              className="mt-1 w-28 border border-gray-300 rounded-lg px-3 py-2 text-sm tabular-nums"
            />
          </label>
          <label className="flex flex-col text-xs text-gray-500">
            People cap
            <input
              value={peopleCap}
              onChange={(e) => setPeopleCap(e.target.value)}
              disabled={busy}
              placeholder="default"
              inputMode="numeric"
              className="mt-1 w-28 border border-gray-300 rounded-lg px-3 py-2 text-sm tabular-nums"
            />
          </label>
          <button
            type="button"
            onClick={() => onRunEnrichment({ provider, companyCap, peopleCap })}
            disabled={busy}
            className="px-4 py-2 rounded-lg text-sm font-semibold text-white disabled:opacity-60 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
            style={{ background: TEAL, '--tw-ring-color': TEAL }}
          >
            {busy ? 'Running…' : 'Run enrichment'}
          </button>
        </div>
        <p className="text-xs text-gray-400 mt-3">
          Start with a small company cap and the <code>deterministic</code> provider to check the wiring.
        </p>
      </section>

      <section className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
        <h2 className="text-sm font-semibold text-gray-900">Run one enricher</h2>
        <p className="text-xs text-gray-500 mt-1">
          Fill a single field over its backlog. Use this to try one step without paying for the other six.
        </p>
        <div className="flex flex-wrap items-end gap-4 mt-4">
          <label className="flex flex-col text-xs text-gray-500">
            Enricher
            <select
              value={enricher}
              onChange={(e) => setEnricher(e.target.value)}
              disabled={busy}
              className="mt-1 border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-900 bg-white"
            >
              {ENRICHERS.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.label}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col text-xs text-gray-500">
            Provider
            <select
              value={oneProvider}
              onChange={(e) => setOneProvider(e.target.value)}
              disabled={busy}
              className="mt-1 border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-900 bg-white"
            >
              <option value="deterministic">deterministic (no spend)</option>
              <option value="openai">openai</option>
              <option value="ollama">ollama</option>
            </select>
          </label>
          <label className="flex flex-col text-xs text-gray-500">
            Limit
            <input
              value={oneLimit}
              onChange={(e) => setOneLimit(e.target.value)}
              disabled={busy}
              placeholder="all"
              inputMode="numeric"
              className="mt-1 w-28 border border-gray-300 rounded-lg px-3 py-2 text-sm tabular-nums"
            />
          </label>
          <button
            type="button"
            onClick={() => onRunEnricher({ enricher, provider: oneProvider, companyLimit: oneLimit })}
            disabled={busy}
            className="px-4 py-2 rounded-lg text-sm font-semibold text-white disabled:opacity-60 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
            style={{ background: TEAL, '--tw-ring-color': TEAL }}
          >
            {busy ? 'Running…' : 'Run enricher'}
          </button>
        </div>

        <p
          className={`text-xs mt-3 rounded-lg px-3 py-2 border ${
            oneSpend.spends
              ? 'bg-amber-50 border-amber-200 text-amber-900'
              : 'bg-gray-50 border-gray-200 text-gray-600'
          }`}
        >
          {spendSummary(enricher, oneProvider, oneLimit)}
        </p>
        {selected && <p className="text-xs text-gray-400 mt-2">{selected.note}</p>}
      </section>

    </div>
  );
}
